import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, Fingerprint, Eye, EyeOff, Shield, X, Check, 
  KeyRound, Smartphone, AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Security verification context - stores temporary security token
let securityToken = null;
let tokenExpiry = null;

export function isSecurityVerified() {
  if (!securityToken || !tokenExpiry) return false;
  return new Date() < new Date(tokenExpiry);
}

export function clearSecurityToken() {
  securityToken = null;
  tokenExpiry = null;
}

export function setSecurityToken(token, expiresIn) {
  securityToken = token;
  tokenExpiry = new Date(Date.now() + expiresIn * 1000);
}

// Hook to check if user has security set up
export function useSecurityStatus() {
  const [status, setStatus] = useState({ 
    hasPin: false, 
    biometricEnabled: false, 
    twoFactorEnabled: false,
    twoFactorMethod: null,
    biometricLoginEnabled: false,
    loading: true 
  });
  
  useEffect(() => {
    const checkStatus = async () => {
      const token = localStorage.getItem('session_token');
      if (!token) {
        setStatus({ 
          hasPin: false, 
          biometricEnabled: false, 
          twoFactorEnabled: false,
          twoFactorMethod: null,
          biometricLoginEnabled: false,
          loading: false 
        });
        return;
      }
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/security/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStatus({
          hasPin: response.data.has_pin,
          biometricEnabled: response.data.biometric_enabled,
          twoFactorEnabled: response.data.two_factor_enabled,
          twoFactorMethod: response.data.two_factor_method,
          biometricLoginEnabled: response.data.biometric_login_enabled,
          loading: false
        });
      } catch (error) {
        console.error('Failed to get security status:', error);
        setStatus({ 
          hasPin: false, 
          biometricEnabled: false, 
          twoFactorEnabled: false,
          twoFactorMethod: null,
          biometricLoginEnabled: false,
          loading: false 
        });
      }
    };
    
    checkStatus();
  }, []);
  
  return status;
}

// PIN Setup Dialog
export function PINSetupDialog({ open, onClose, onSuccess }) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState(1); // 1 = enter PIN, 2 = confirm PIN
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const inputRef = useRef(null);
  
  useEffect(() => {
    if (open) {
      setPin('');
      setConfirmPin('');
      setStep(1);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);
  
  const handlePinChange = (value, isConfirm = false) => {
    // Only allow digits
    const cleanValue = value.replace(/\D/g, '').slice(0, 6);
    if (isConfirm) {
      setConfirmPin(cleanValue);
    } else {
      setPin(cleanValue);
    }
  };
  
  const handleNext = () => {
    if (pin.length < 4) {
      toast.error('PIN must be at least 4 digits');
      return;
    }
    setStep(2);
    setTimeout(() => inputRef.current?.focus(), 100);
  };
  
  const handleSetup = async () => {
    if (pin !== confirmPin) {
      toast.error('PINs do not match');
      setConfirmPin('');
      return;
    }
    
    setLoading(true);
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/security/setup-pin`,
        { pin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Security PIN set up successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to set up PIN:', error);
      toast.error(error.response?.data?.detail || 'Failed to set up PIN');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-gradient-to-br from-slate-900 via-[#0F1E36] to-slate-900 border-2 border-[#2B4C7E]/30 rounded-3xl text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl text-white">
            <div className="w-12 h-12 bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] rounded-2xl flex items-center justify-center">
              <Shield className="h-6 w-6" />
            </div>
            <span className="bg-gradient-to-r from-[#3A5C8F] to-[#D0B384] bg-clip-text text-transparent font-bold">
              Set Up Security PIN
            </span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-6 space-y-6">
          {/* Step indicator */}
          <div className="flex justify-center gap-2">
            <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-[#2B4C7E]' : 'bg-gray-600'}`} />
            <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-[#2B4C7E]' : 'bg-gray-600'}`} />
          </div>
          
          <div className="text-center">
            <p className="text-gray-300 text-sm mb-6">
              {step === 1 
                ? 'Create a 4-6 digit PIN to protect sensitive actions'
                : 'Confirm your PIN'}
            </p>
            
            {/* PIN Input */}
            <div className="relative max-w-[200px] mx-auto">
              <Input
                ref={inputRef}
                type={showPin ? 'text' : 'password'}
                value={step === 1 ? pin : confirmPin}
                onChange={(e) => handlePinChange(e.target.value, step === 2)}
                placeholder="••••••"
                className="text-center text-2xl tracking-[0.5em] bg-white/10 border-[#2B4C7E]/30 text-white placeholder:text-gray-500 rounded-xl h-14"
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            
            {/* PIN dots indicator */}
            <div className="flex justify-center gap-2 mt-4">
              {[...Array(6)].map((_, i) => (
                <div 
                  key={i}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    (step === 1 ? pin : confirmPin).length > i 
                      ? 'bg-[#2B4C7E]' 
                      : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex gap-3">
            {step === 2 && (
              <Button
                variant="outline"
                onClick={() => {
                  setStep(1);
                  setConfirmPin('');
                }}
                className="flex-1 rounded-full border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                Back
              </Button>
            )}
            <Button
              onClick={step === 1 ? handleNext : handleSetup}
              disabled={loading || (step === 1 ? pin.length < 4 : confirmPin.length < 4)}
              className="flex-1 bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full hover:opacity-90"
            >
              {loading ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                  <Lock className="h-5 w-5" />
                </motion.div>
              ) : step === 1 ? (
                'Next'
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  Set PIN
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Security Verification Dialog
export default function SecurityVerification({ 
  open, 
  onClose, 
  onVerified,
  title = "Security Verification Required",
  description = "Please verify your identity to continue"
}) {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const inputRef = useRef(null);
  const { hasPin, biometricEnabled } = useSecurityStatus();
  
  useEffect(() => {
    if (open) {
      setPin('');
      setError('');
      checkBiometricAvailability();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);
  
  const checkBiometricAvailability = async () => {
    // Check if WebAuthn is available
    if (window.PublicKeyCredential) {
      try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setBiometricAvailable(available);
      } catch (e) {
        setBiometricAvailable(false);
      }
    }
  };
  
  const handlePinChange = (value) => {
    const cleanValue = value.replace(/\D/g, '').slice(0, 6);
    setPin(cleanValue);
    setError('');
  };
  
  const verifyWithPin = async () => {
    if (pin.length < 4) {
      setError('Please enter your PIN');
      return;
    }
    
    setLoading(true);
    setError('');
    const token = localStorage.getItem('session_token');
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/security/verify-pin`,
        { pin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.verified) {
        setSecurityToken(response.data.security_token, response.data.expires_in);
        toast.success('Verified successfully');
        onVerified?.();
        // Don't call onClose here - let onVerified handle it
      }
    } catch (error) {
      console.error('PIN verification failed:', error);
      setError(error.response?.data?.detail || 'Invalid PIN');
      setPin('');
    } finally {
      setLoading(false);
    }
  };
  
  const verifyWithBiometric = async () => {
    if (!biometricAvailable) return;
    
    setBiometricLoading(true);
    
    try {
      // Create a challenge for WebAuthn
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);
      
      // Request biometric authentication
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge,
          timeout: 60000,
          userVerification: 'required',
          rpId: window.location.hostname,
        }
      });
      
      if (credential) {
        // For simplicity, we'll just verify that biometric succeeded
        // In production, you'd verify the credential on the server
        const token = localStorage.getItem('session_token');
        
        // Use a special endpoint or just set verified status
        const response = await axios.post(
          `${BACKEND_URL}/api/security/verify-pin`,
          { pin: 'biometric' }, // Special marker
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // If biometric worked but backend needs PIN, fall back to PIN
        setSecurityToken('biometric_verified', 300);
        toast.success('Biometric verification successful');
        onVerified?.();
        // Don't call onClose here - let onVerified handle it
      }
    } catch (error) {
      console.error('Biometric verification failed:', error);
      // Biometric failed, user can still use PIN
      toast.error('Biometric verification failed. Please use your PIN.');
    } finally {
      setBiometricLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-gradient-to-br from-slate-900 via-[#0F1E36] to-slate-900 border-2 border-[#2B4C7E]/30 rounded-3xl text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl text-white">
            <motion.div 
              className="w-12 h-12 bg-gradient-to-br from-[#C8A97E] to-[#C8A97E] rounded-2xl flex items-center justify-center"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Lock className="h-6 w-6" />
            </motion.div>
            <span className="bg-gradient-to-r from-[#D0B384] to-[#D0B384] bg-clip-text text-transparent font-bold">
              {title}
            </span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-6">
          <p className="text-gray-300 text-sm text-center">
            {description}
          </p>
          
          {/* Biometric option */}
          {biometricAvailable && biometricEnabled && (
            <motion.button
              onClick={verifyWithBiometric}
              disabled={biometricLoading}
              className="w-full p-4 bg-gradient-to-r from-[#2B4C7E]/20 to-[#C8A97E]/20 border border-[#2B4C7E]/30 rounded-2xl flex items-center justify-center gap-3 hover:from-[#2B4C7E]/30 hover:to-[#C8A97E]/30 transition-colors disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {biometricLoading ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                  <Fingerprint className="h-8 w-8 text-[#3A5C8F]" />
                </motion.div>
              ) : (
                <>
                  <Fingerprint className="h-8 w-8 text-[#3A5C8F]" />
                  <div className="text-left">
                    <p className="font-medium">Use Face ID / Touch ID</p>
                    <p className="text-xs text-gray-400">Quick and secure</p>
                  </div>
                </>
              )}
            </motion.button>
          )}
          
          {/* Divider */}
          {biometricAvailable && biometricEnabled && (
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gray-700" />
              <span className="text-xs text-gray-500">or enter PIN</span>
              <div className="flex-1 h-px bg-gray-700" />
            </div>
          )}
          
          {/* PIN Input */}
          <div className="space-y-4">
            <div className="relative">
              <Input
                ref={inputRef}
                type={showPin ? 'text' : 'password'}
                value={pin}
                onChange={(e) => handlePinChange(e.target.value)}
                placeholder="Enter PIN"
                className={`text-center text-2xl tracking-[0.5em] bg-white/10 border-[#2B4C7E]/30 text-white placeholder:text-gray-500 rounded-xl h-14 ${error ? 'border-red-500' : ''}`}
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
                onKeyDown={(e) => e.key === 'Enter' && verifyWithPin()}
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            
            {/* Error message */}
            {error && (
              <motion.p 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400 text-sm text-center flex items-center justify-center gap-2"
              >
                <AlertTriangle className="h-4 w-4" />
                {error}
              </motion.p>
            )}
            
            {/* PIN dots indicator */}
            <div className="flex justify-center gap-2">
              {[...Array(6)].map((_, i) => (
                <div 
                  key={i}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    pin.length > i ? 'bg-[#C8A97E]' : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>
          
          {/* Verify button */}
          <Button
            onClick={verifyWithPin}
            disabled={loading || pin.length < 4}
            className="w-full bg-gradient-to-r from-[#C8A97E] to-[#C8A97E] text-white rounded-full py-6 hover:opacity-90"
          >
            {loading ? (
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                <Lock className="h-5 w-5" />
              </motion.div>
            ) : (
              <>
                <KeyRound className="h-5 w-5 mr-2" />
                Verify PIN
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// HOC to wrap protected actions
export function withSecurityVerification(WrappedComponent) {
  return function SecurityProtectedComponent(props) {
    const [showVerification, setShowVerification] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const { hasPin } = useSecurityStatus();
    
    const requireVerification = (action) => {
      if (!hasPin) {
        // No PIN set up, allow action
        action();
        return;
      }
      
      if (isSecurityVerified()) {
        // Already verified
        action();
        return;
      }
      
      // Need verification
      setPendingAction(() => action);
      setShowVerification(true);
    };
    
    const handleVerified = () => {
      setShowVerification(false);
      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
    };
    
    return (
      <>
        <WrappedComponent {...props} requireVerification={requireVerification} />
        <SecurityVerification
          open={showVerification}
          onClose={() => {
            setShowVerification(false);
            setPendingAction(null);
          }}
          onVerified={handleVerified}
        />
      </>
    );
  };
}
